import React, { useState, useEffect } from 'react'
import { IntegrationProviders, IntegrationSyncResourceTypes } from 'app-constants'
import IntegrationFieldSelector from 'components/integrations/components/fieldSelector'
import MortaIntegration from 'components/integrations/components/morta'
import { useAuth } from 'hooks/auth'
import { useProject } from 'hooks/useProject'
import { INTEGRATIONS } from 'routes'
import Button from 'components/button'
import api from 'helpers/api'
import { IntegrationButtonProps, IIntegrationSyncResourceTypes, IIntegrationCatalogueField } from 'types'
import { isAccountConnected } from 'helpers/integration'
import { useApplicationStore } from 'hooks/application'

interface IntegrationProps {
  provider?: IntegrationProviders
  type: IIntegrationSyncResourceTypes
  buttonLabel: string
  buttonFunction: (props: IntegrationButtonProps) => void
  buttonLoading: boolean
  projectIds?: string[]
  viewIds?: string[]
  syncAllHubs?: boolean
  // Drops the large leading top margin — used when the form is embedded directly under
  // other content (e.g. the create-table card) rather than below a provider image in a modal.
  compact?: boolean
}

const Integration: React.FC<IntegrationProps> = ({
  provider,
  type,
  buttonLabel,
  buttonFunction,
  buttonLoading,
  projectIds,
  viewIds,
  syncAllHubs: syncAllHubsProp,
  compact = false
}) => {
  const { user } = useAuth()
  const mainProject = useProject()
  const { displayErrorMessage } = useApplicationStore()
  const isMortaProjectSync = provider === IntegrationProviders.MORTA && type !== IntegrationSyncResourceTypes.VIEWS
  const initialSyncAllHubs = isMortaProjectSync ? (syncAllHubsProp ?? false) : false

  // Field selector state
  const [fields, setFields] = useState<IIntegrationCatalogueField[]>([])
  const [selectedValues, setSelectedValues] = useState<Record<string, string[]>>({})
  const [providerDisplayName, setProviderDisplayName] = useState<string>('')

  // Morta states (kept separate since Morta uses internal projects/views)
  const [projects, setProjects] = useState<string[]>(
    isMortaProjectSync ? (initialSyncAllHubs ? [] : projectIds ? [...projectIds] : [mainProject.project.publicId]) : []
  )
  const [views, setViews] = useState<string[]>(
    provider === IntegrationProviders.MORTA && type === IntegrationSyncResourceTypes.VIEWS
      ? viewIds
        ? [...viewIds]
        : []
      : []
  )
  const [syncAllHubs, setSyncAllHubs] = useState<boolean>(initialSyncAllHubs)

  // Fetch fields from the catalogue endpoint when provider changes
  useEffect(() => {
    if (!provider || provider === IntegrationProviders.MORTA) {
      setFields([])
      return
    }

    api
      .getIntegrationCatalogue()
      .then((response) => {
        const providerConfig = response.data[provider]
        if (providerConfig) {
          setFields(providerConfig.fields ?? [])
          setProviderDisplayName(providerConfig.display)
        } else {
          setFields([])
          setProviderDisplayName('')
        }
      })
      .catch((error) => {
        displayErrorMessage(error)
        setFields([])
        setProviderDisplayName('')
      })

    // Reset selected values when provider changes
    setSelectedValues({})
  }, [provider, displayErrorMessage])

  const accountConnected = isAccountConnected(user, provider)

  // Check if all required fields have values (exclude fields hidden by visibleForTypes)
  const requiredFieldsSatisfied = fields
    .filter((field) => field.required && (!field.visibleForTypes || field.visibleForTypes.includes(type)))
    .every((field) => {
      const values = selectedValues[field.key]
      return values && values.length > 0
    })

  const buttonDisabled =
    !provider ||
    // For non-Morta providers, check if required fields are satisfied
    (provider !== IntegrationProviders.MORTA && fields.length > 0 && !requiredFieldsSatisfied) ||
    // Morta-specific validation
    (provider === IntegrationProviders.MORTA &&
      type !== IntegrationSyncResourceTypes.VIEWS &&
      !syncAllHubs &&
      projects.length === 0) ||
    (provider === IntegrationProviders.MORTA && type === IntegrationSyncResourceTypes.VIEWS && views.length === 0)

  // Build args from selected field values for the button function
  const buildButtonArgs = (): IntegrationButtonProps => {
    if (provider === IntegrationProviders.MORTA) {
      return { projects, views, syncAllHubs }
    }

    const args: IntegrationButtonProps = {}
    fields.forEach((field) => {
      const values = selectedValues[field.key]
      if (values && values.length > 0) {
        // Map field keys to expected props
        const value = field.type === 'multiselect' ? values : values[0]
        // Use type assertion to allow dynamic property assignment
        ;(args as Record<string, string | string[] | undefined>)[field.key] = value
      }
    })
    return args
  }

  if (!buttonLoading) {
    return (
      <div>
        {/* In compact (create-table) mode the caller has already filtered to connected
            accounts, so the "successfully connected" confirmation is redundant — only surface
            this block when the account isn't connected (a prompt to go and connect one). */}
        {provider && provider !== IntegrationProviders.MORTA && !(compact && accountConnected) && (
          <div>
            <div className={`font-bold ${compact ? 'mt-0' : 'mt-[50px]'} mb-2.5`}>Connected Account</div>
            {accountConnected ? (
              <div>You have a successfully connected account</div>
            ) : (
              <div>
                You do not have a connected {providerDisplayName || 'Unknown'} account. Go to your{' '}
                <a href={INTEGRATIONS}>integration settings</a> to configure.
              </div>
            )}
          </div>
        )}

        {accountConnected && provider && provider !== IntegrationProviders.MORTA && fields.length > 0 && (
          <IntegrationFieldSelector
            provider={provider}
            fields={fields}
            selectedValues={selectedValues}
            onFieldChange={(key, values) => {
              setSelectedValues((prev) => ({ ...prev, [key]: values }))
            }}
            type={type}
            compact={compact}
          />
        )}

        {provider === IntegrationProviders.MORTA && (
          <MortaIntegration
            type={type}
            selectedProjects={projects}
            setSelectedProjects={setProjects}
            selectedViews={views}
            setSelectedViews={setViews}
            syncAllHubs={syncAllHubs}
            setSyncAllHubs={setSyncAllHubs}
            compact={compact}
          />
        )}

        {accountConnected && (
          <div className={`flex items-center text-red ${compact ? 'mt-5' : 'mt-[50px]'}`}>
            <i className="mr-[5px]">* Required Field.</i>

            <Button
              className="ml-auto text-base"
              onClick={() => buttonFunction(buildButtonArgs())}
              isLoading={buttonLoading}
              disabled={buttonDisabled}
              internalType="accept"
            >
              {buttonLabel}
            </Button>
          </div>
        )}
      </div>
    )
  } else {
    return (
      <div>Your integration is being built. This may take some time and you will be emailed when it is complete.</div>
    )
  }
}

export default Integration
